/**
 * Portail fournisseur Fortemix, lu à la place d'une saisie manuelle.
 *
 * Fortemix publie sur `customer.fortemix.eu` la date de production et le lien de
 * suivi de chaque commande, bien avant de les communiquer par mail. Les relever
 * évite d'attendre — et surtout évite qu'ils ne soient jamais saisis.
 *
 * Ce fichier ne contient QUE des fonctions pures : la reconnaissance des
 * commandes concernées, la fabrication des identifiants à essayer, et la lecture
 * d'une ligne du tableau. Le réseau et le HTML vivent côté API. C'est la partie
 * qui peut se tromper en silence, donc celle qui doit être testable sans portail.
 *
 * **Tout ce qui suit est relevé sur le portail réel le 18/09/2026**, sur les 27
 * commandes Fortemix non livrées de la base — pas sur une documentation.
 */
/**
 * Les gammes que Fortemix fabrique, telles qu'elles apparaissent en référence.
 *
 * `FCK` (Fortelock) et `UTM` couvrent l'essentiel de ses commandes — mais pas
 * toutes : une commande FORTEMIX peut ne porter que du transport (`LOG.1`), un
 * accessoire (`EVA.1`) ou un produit d'une autre gamme (`UZN.430-8L`).
 */
const GAMMES_FORTEMIX = /FCK|UTM/i;
/** Le nom du fournisseur, tel qu'il figure à l'annuaire et sur les commandes. */
const NOM_FORTEMIX = 'FORTEMIX';
/**
 * `true` si la commande relève du portail Fortemix.
 *
 * **Deux critères, et il en faut deux.** Le fournisseur d'abord : c'est le fait,
 * et il tranche seul quand il est renseigné. Les références ensuite, comme
 * REPLI — une commande dont l'import n'a pas reconnu le fournisseur doit tout de
 * même être relevée si ses articles sont des Fortelock.
 *
 * Le repli était au départ le seul critère, et c'était un défaut : quatre
 * commandes FORTEMIX en cours — dont `FAC-20260826-39440`, absente de leur
 * portail depuis le 2 septembre — n'étaient jamais examinées, parce que leurs
 * références ne portent ni `FCK` ni `UTM`. Un critère de repli employé comme
 * critère principal laisse passer exactement ce qu'il était censé rattraper.
 */
export function estCommandeFortemix(references, fournisseurs) {
    const parFournisseur = (fournisseurs ?? [])
        .some((f) => String(f ?? '').trim().toUpperCase() === NOM_FORTEMIX);
    if (parFournisseur)
        return true;
    return contientProduitFortelock(references);
}
/**
 * `true` si la commande porte au moins un produit des gammes Fortelock.
 *
 * **Strictement les RÉFÉRENCES**, sans regarder le fournisseur — et c'est ce
 * qui la distingue d'`estCommandeFortemix`. Les deux questions se ressemblent
 * et ne sont pas la même :
 *
 * - « faut-il interroger le portail Fortemix ? » — le fournisseur tranche, et
 *   les références ne sont qu'un repli pour les commandes dont l'import n'a
 *   rien reconnu. C'est `estCommandeFortemix` ;
 * - « est-ce une commande Fortelock ? » — seul le produit compte. C'est ici.
 *
 * Trois commandes FORTEMIX en cours ne portent aucune référence `FCK` ni
 * `UTM` — du transport (`LOG.1`), des accessoires (`EVA.1`, `EVA.100`) — et
 * séparent donc les deux critères. Employer l'un pour l'autre n'est pas une
 * nuance de vocabulaire : cela change ce que l'écran propose.
 */
export function contientProduitFortelock(references) {
    return references.some((r) => GAMMES_FORTEMIX.test(String(r ?? '')));
}
/**
 * Les identifiants à essayer sur `?page=orders&CustomerId=…`, dans l'ordre.
 *
 * Mosaik et Fortemix ne nomment pas les commandes pareil, et pas toujours de la
 * même façon d'une commande à l'autre : la référence saisie chez Fortemix est
 * tantôt la nôtre en entier, tantôt son seul numéro, tantôt sa date collée au
 * numéro. Les trois formes sont ATTESTÉES le 18/09/2026, chacune gagnant sur des
 * commandes différentes :
 *
 * | Référence Mosaik | Ce que Fortemix connaît |
 * |---|---|
 * | `FAC-20260808-39419` | `FAC-20260808-39419` — la référence entière |
 * | `FAC-20260915-39488` | `39488` — le seul numéro |
 * | `FAC-20260831-39457` | `20260831-39457` — sans le préfixe |
 * | `#MF3371` | `MF3371` — sans le croisillon |
 *
 * D'où l'essai en cascade, repris du scénario n8n : on s'arrête au premier qui
 * rend une ligne. Deviner laquelle est la bonne coûterait un aller-retour de
 * plus dans le meilleur des cas, et une commande manquée dans le pire.
 */
export function identifiantsFortemix(ref) {
    const nom = String(ref ?? '').trim();
    if (!nom)
        return [];
    const formes = [nom.replace('#', '')];
    // Les deux replis ne valent que pour les références en `FAC-<date>-<numéro>` :
    // sur `#MF3371` ils produiraient `MF3371` deux fois, ou une chaîne vide.
    if (nom.includes('FAC') && nom.includes('-')) {
        formes.push(nom.split('-').pop() ?? '');
        formes.push(nom.substring(4));
    }
    return [...new Set(formes.map((f) => f.trim()).filter(Boolean))];
}
/** La liste des commandes du portail Fortemix, filtrée sur un identifiant. */
export function urlPortailFortemix(identifiant) {
    return `https://customer.fortemix.eu/?page=orders&CustomerId=${encodeURIComponent(identifiant)}`;
}
/**
 * Rang de la date d'expédition dans la cellule `.order_id`.
 *
 * L'en-tête du tableau annonce `Id / Received / Production / Shipping /
 * Parent-Child`, et c'est **Production** — le rang 2 — qui est retenu, comme le
 * faisait le scénario n8n.
 *
 * Ce n'est pas un choix par défaut : `Shipping` ne se remplit qu'une fois le
 * camion parti, alors que `Production` est la date que Fortemix ANNONCE. C'est
 * elle qui permet de prévenir le client avant le départ, et elle correspond au
 * sens de `dateExpedition` chez Mosaik — une date future met la commande en
 * « planifiée », une date passée en « expédiée ». Relevé le 18/09/2026 : les
 * deux colonnes coïncident dès que l'envoi est parti, et `Shipping` est vide
 * partout ailleurs.
 */
const RANG_DATE_EXPEDITION = 2;
/** `JJ.MM.AAAA` → `AAAA-MM-JJ`. Le tiret de Fortemix vaut « pas de date ». */
function jourDepuisDateTcheque(valeur) {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(valeur ?? '').trim());
    return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}
/**
 * Paramètres sous lesquels les transporteurs portent le numéro dans leur URL.
 *
 * Fortemix ne publie pas le numéro de suivi : il publie le LIEN vers la page du
 * transporteur, numéro compris. Les noms ci-dessous sont exactement ceux que
 * `urlSuiviTransporteur` construit de son côté — la boucle est donc fermée, et
 * un transporteur ajoutée ici doit l'être là-bas aussi.
 */
const PARAMETRES_NUMERO = ['search', 'refNumber', 'trknbr', 'tracking-id'];
/**
 * Numéro de suivi caché dans une URL de transporteur.
 *
 * Rendre l'URL telle quelle — ce que faisait le scénario n8n — serait une
 * régression : toute la chaîne de suivi de Mosaik travaille sur le NUMÉRO. Le
 * format décide du transporteur, ouvre la page Dachser, et se compare d'un
 * relevé à l'autre. Une URL n'y sert à rien.
 */
export function numeroDepuisUrlSuivi(url) {
    const brut = String(url ?? '').trim();
    if (!brut)
        return null;
    const question = brut.indexOf('?');
    if (question < 0)
        return null;
    const parametres = new URLSearchParams(brut.slice(question + 1));
    for (const nom of PARAMETRES_NUMERO) {
        const valeur = parametres.get(nom)?.trim();
        if (valeur)
            return valeur;
    }
    return null;
}
/**
 * « Ce contrat n'a pas de suivi », tel que Fortemix l'écrit.
 *
 * Distinct de « No status available », qui signifie « pas encore d'événement »
 * mais s'accompagne bel et bien d'un lien : celui-là se lit.
 */
const SANS_SUIVI = /NO TRACKING/;
/** Numéro FedEx dans une note de transport : trois groupes de quatre chiffres. */
const NUMERO_FEDEX = /(\d{4}[\s-]?\d{4}[\s-]?\d{4})/;
/**
 * Traduit une ligne du tableau Fortemix en date d'expédition et numéro de suivi.
 *
 * Deux chemins pour le suivi, et l'ordre compte : FedEx n'a pas de lien sur le
 * portail, son numéro est écrit à la main dans la note de transport. Le tester
 * d'abord évite de chercher une URL qui n'existe pas.
 */
export function lireLigneFortemix(ligne) {
    const dateExpedition = jourDepuisDateTcheque(ligne.lignesIdentite[RANG_DATE_EXPEDITION]);
    if (/fedex/i.test(ligne.noteTransport)) {
        const m = NUMERO_FEDEX.exec(ligne.noteTransport);
        // Les espaces ne font pas partie du numéro : les garder donnerait un lien mort.
        return { dateExpedition, tracking: m ? m[1].replace(/[\s-]/g, '') : null };
    }
    if (SANS_SUIVI.test(ligne.statutHtml))
        return { dateExpedition, tracking: null };
    const url = /https?:\/\/[^\s'"]+/.exec(ligne.statutHtml)?.[0] ?? null;
    return { dateExpedition, tracking: numeroDepuisUrlSuivi(url) };
}
//# sourceMappingURL=fortemix.js.map